<template>
  <b-overlay
    variant="white"
    :show="isLoading && !range"
    spinner-variant="primary"
    blur="0"
    opacity=".75"
    rounded="sm"
  >
    <!-- Error -->
    <div v-if="loadError && !isLoading" class="brd-error">
      <feather-icon icon="AlertCircleIcon" size="40" class="brd-error__icon" />
      <h5 class="mb-50">{{ $t('Could not load range') }}</h5>
      <p class="text-muted mb-1">{{ loadError }}</p>
      <b-button variant="outline-primary" size="sm" @click="loadRange">
        <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
        {{ $t('Retry') }}
      </b-button>
    </div>

    <div v-else-if="range" class="barcode-range-detail">
      <!-- Back button -->
      <router-link
        :to="{ name: 'barcode_pool_list' }"
        class="brd-back-btn mb-2 mt-1 d-inline-flex align-items-center text-body"
      >
        <feather-icon icon="ArrowLeftIcon" size="18" class="mr-50" />
        <span class="font-weight-bolder font-medium-1">{{ $t('Back') }}</span>
      </router-link>

      <h2 class="font-weight-bolder mb-2">{{ $t('Barcode Range Detail') }}</h2>
      <!-- Unified hero: status pills + title + metadata + consumption -->
      <b-card no-body class="brd-hero">
        <div class="brd-hero__body">
          <div class="brd-hero__top">
            <div class="brd-hero__pills">
              <b-badge
                :variant="range.status === 'active' ? 'light-success' : 'light-dark'"
                class="brd-status-badge"
              >
                <span
                  class="brd-status-badge__dot"
                  :class="`brd-status-badge__dot--${range.status}`"
                ></span>
                {{ $t(range.status === 'active' ? 'Active' : 'Exhausted') }}
              </b-badge>
              <b-badge variant="light-secondary" class="brd-provider-pill">
                <img
                  :src="providerLogo"
                  :alt="range.provider_name"
                  class="brd-provider-pill__logo"
                />
                {{ range.provider_name }}
              </b-badge>
              <b-badge v-if="range.barcode_type" variant="light-secondary" class="brd-type-pill">
                {{ formatBarcodeType(range.barcode_type) }}
              </b-badge>
              <b-badge :variant="getAlertVariant(alertLevel)" pill>
                {{ $t(`Alert: ${alertLevel}`) }}
              </b-badge>
            </div>

            <b-dropdown
              v-if="isAdmin && !range.deleted_at"
              variant="outline-secondary"
              right
              class="brd-actions"
            >
              <template #button-content>
                {{ $t('Actions') }}
              </template>
              <b-dropdown-item variant="danger" @click="confirmDelete">
                <feather-icon icon="Trash2Icon" size="14" class="mr-50" />
                {{ $t('Delete Range') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>

          <h1 class="brd-hero__title">
            {{ range.range_start }}
            <feather-icon icon="ArrowRightIcon" size="22" class="mx-25" />
            {{ range.range_end }}
          </h1>

          <div class="brd-hero__meta">
            <span class="brd-hero__meta-item">
              <span class="brd-hero__meta-label">{{ $t('Customer') }}</span>
              <span class="brd-hero__meta-sep">·</span>
              <router-link
                v-if="isAdmin && range.customer_id"
                :to="{ name: 'customers_detail', params: { id: range.customer_id } }"
                class="brd-ref-link"
              >
                {{ range.customer_name }}
              </router-link>
              <span v-else>{{ range.customer_name || '—' }}</span>
            </span>
            <span class="brd-hero__meta-item">
              <span class="brd-hero__meta-label">{{ $t('Created At') }}</span>
              <span class="brd-hero__meta-sep">·</span>
              {{ formatDateTime(range.created_at) }}
            </span>
            <span class="brd-hero__meta-item">
              <span class="brd-hero__meta-label">{{ $t('Total') }}</span>
              <span class="brd-hero__meta-sep">·</span>
              <strong>{{ formatNumber(range.total_count) }}</strong>
            </span>
            <span v-if="range.exhausted_at" class="brd-hero__meta-item">
              <span class="brd-hero__meta-label">{{ $t('Exhausted At') }}</span>
              <span class="brd-hero__meta-sep">·</span>
              {{ formatDateTime(range.exhausted_at) }}
            </span>
            <span v-if="range.deleted_at" class="brd-hero__meta-item text-danger">
              <span class="brd-hero__meta-label">{{ $t('Deleted At') }}</span>
              <span class="brd-hero__meta-sep">·</span>
              {{ formatDateTime(range.deleted_at) }}
            </span>
          </div>

          <div class="brd-consumption">
            <div class="brd-consumption__header">
              <span class="brd-consumption__title">{{ $t('Consumption') }}</span>
              <span class="brd-consumption__stat">
                <strong>{{ formatNumber(range.used_count) }}</strong>
                <span class="text-muted"> {{ $t('used') }} · {{ usedPercentage }}% </span>
              </span>
            </div>
            <div class="brd-stacked-bar">
              <div
                v-if="range.used_count"
                class="brd-stacked-bar__seg brd-stacked-bar__seg--used"
                :style="{ width: `${segmentPct(range, 'used_count')}%` }"
                :title="`${$t('Used')}: ${formatNumber(range.used_count)}`"
              />
              <div
                v-if="range.reserved_count"
                class="brd-stacked-bar__seg brd-stacked-bar__seg--reserved"
                :style="{ width: `${segmentPct(range, 'reserved_count')}%` }"
                :title="`${$t('Reserved')}: ${formatNumber(range.reserved_count)}`"
              />
              <div
                v-if="range.skipped_count"
                class="brd-stacked-bar__seg brd-stacked-bar__seg--skipped"
                :style="{ width: `${segmentPct(range, 'skipped_count')}%` }"
                :title="`${$t('Skipped')}: ${formatNumber(range.skipped_count)}`"
              />
            </div>
            <div class="brd-consumption__legend">
              <span class="brd-legend-item">
                <span class="brd-legend-dot brd-legend-dot--available"></span>
                {{ $t('Available') }}
                <strong>{{ formatNumber(range.available_count) }}</strong>
              </span>
              <span class="brd-legend-item">
                <span class="brd-legend-dot brd-legend-dot--reserved"></span>
                {{ $t('Reserved') }}
                <strong>{{ formatNumber(range.reserved_count) }}</strong>
              </span>
              <span class="brd-legend-item">
                <span class="brd-legend-dot brd-legend-dot--used"></span>
                {{ $t('Used') }}
                <strong>{{ formatNumber(range.used_count) }}</strong>
              </span>
              <span class="brd-legend-item">
                <span class="brd-legend-dot brd-legend-dot--skipped"></span>
                {{ $t('Skipped') }}
                <strong>{{ formatNumber(range.skipped_count) }}</strong>
              </span>
            </div>
          </div>
        </div>
      </b-card>

      <!-- Barcodes panel -->
      <b-card no-body class="brd-panel-card">
        <barcode-range-barcodes-panel :range="range" />
      </b-card>
    </div>
  </b-overlay>
</template>

<script>
import { BCard, BButton, BBadge, BOverlay, BDropdown, BDropdownItem } from 'bootstrap-vue';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';
import { getProviderLogo } from '@/libs/providerHelper';
import barcodePoolFormatters from '@/views/pages/barcode-pool/barcodePoolFormatters.mixin';
import BarcodeRangeBarcodesPanel from '@/views/pages/barcode-pool/BarcodeRangeBarcodesPanel.vue';
import { computeAlertLevel, getAlertVariant, segmentPct } from '@/constants/barcodePool';

export default {
  name: 'BarcodeRangeDetail',

  components: {
    BCard,
    BButton,
    BBadge,
    BOverlay,
    BDropdown,
    BDropdownItem,
    BarcodeRangeBarcodesPanel
  },

  mixins: [helper, barcodePoolFormatters],

  data() {
    return {
      isLoading: true,
      loadError: null,
      range: null
    };
  },

  computed: {
    ...mapGetters('auth', ['isAdmin']),

    alertLevel() {
      return computeAlertLevel(this.range);
    },

    usedPercentage() {
      return Math.round(segmentPct(this.range, 'used_count'));
    },

    providerLogo() {
      if (!this.range) return null;
      return getProviderLogo({
        id: this.range.provider_id,
        provider_code: this.range.provider_code
      });
    }
  },

  mounted() {
    this.loadRange();
  },

  methods: {
    segmentPct,
    getAlertVariant,

    loadRange() {
      const { id } = this.$route.params;
      this.isLoading = true;
      this.loadError = null;

      const apiCall = this.isAdmin
        ? useService.barcodePoolService.getBarcodeRangeAdmin(id)
        : useService.barcodePoolService.getBarcodeRangeCustomer(id);

      apiCall
        .then(response => {
          this.range = response.data;
        })
        .catch(messages => {
          this.loadError = this.extractErrorMessage(messages);
          this.range = null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmDelete() {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: this.$t('Delete Range Confirmation'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes'),
        cancelButtonText: this.$t('No'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1'
        },
        buttonsStyling: false
      }).then(result => {
        if (!result.value) return;

        useService.barcodePoolService
          .deleteBarcodeRange(this.range.id)
          .then(response => {
            this.showSuccessMessage(response.message || this.$t('Successful'));
            this.$router.push({ name: 'barcode_pool_list' });
          })
          .catch(messages => {
            this.showBackendErrors(messages);
          });
      });
    }
  }
};
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.barcode-range-detail {
  .brd-back-btn {
    transition: color 0.15s ease, transform 0.15s ease;

    &:hover {
      color: $primary !important;
      transform: translateX(-3px);
    }
  }

  .brd-hero {
    margin-bottom: 1.25rem;

    &__body {
      padding: 1.5rem;
    }

    &__top {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;
      margin-bottom: 1rem;
    }

    &__pills {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      align-items: center;
    }

    &__title {
      font-family: $font-family-monospace;
      font-size: 1.75rem;
      font-weight: 600;
      letter-spacing: -0.01em;
      margin: 0 0 0.85rem 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    &__meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem 1.5rem;
      font-size: 0.875rem;
      margin-bottom: 1.5rem;
      color: $body-color;

      .dark-layout & {
        color: $theme-dark-body-color;
      }
    }

    &__meta-item {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
    }

    &__meta-label {
      color: $text-muted;
      font-weight: 500;
    }

    &__meta-sep {
      color: $text-muted;
      opacity: 0.5;
    }
  }

  .brd-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.7rem;
    font-weight: 500;
    font-size: 0.78rem;

    &__dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }
  }

  .brd-provider-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.6rem 0.3rem 0.4rem;
    font-weight: 500;
    font-size: 0.78rem;

    &__logo {
      width: 16px;
      height: 16px;
      object-fit: contain;
    }
  }

  .brd-type-pill {
    padding: 0.3rem 0.6rem;
    font-weight: 500;
    font-size: 0.78rem;
  }

  .brd-actions {
    flex-shrink: 0;
  }

  .brd-ref-link {
    color: $primary;
    font-weight: 500;
    text-decoration: underline;
    text-decoration-color: rgba($primary, 0.4);
    text-underline-offset: 2px;
    transition: color 0.15s ease, text-decoration-color 0.15s ease;

    &:hover {
      color: darken($primary, 10%);
      text-decoration-color: $primary;
    }
  }

  .brd-consumption {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 0.5rem;
    }

    &__title {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: $text-muted;
      font-weight: 600;
    }

    &__stat {
      font-size: 0.9rem;
      display: inline-flex;
      align-items: baseline;
      gap: 0.35rem;

      strong {
        font-variant-numeric: tabular-nums;
      }
    }

    &__legend {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem 1.5rem;
      margin-top: 0.75rem;
      font-size: 0.82rem;
      color: $text-muted;
    }
  }

  .brd-stacked-bar {
    display: flex;
    height: 10px;
    border-radius: 1rem;
    overflow: hidden;
    background: rgba($success, 0.2);

    &__seg {
      transition: width 0.3s ease;
      min-width: 2px;

      &--used {
        background: $primary;
      }
      &--reserved {
        background: $warning;
      }
      &--skipped {
        background: $secondary;
      }
    }

    .dark-layout & {
      background: rgba($success, 0.25);
    }
  }

  .brd-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;

    strong {
      color: $body-color;
      font-variant-numeric: tabular-nums;
    }

    .dark-layout & strong {
      color: $theme-dark-body-color;
    }
  }

  .brd-legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;

    &--available {
      background: $success;
    }
    &--reserved {
      background: $warning;
    }
    &--used {
      background: $primary;
    }
    &--skipped {
      background: $secondary;
    }
  }

  .brd-panel-card {
    padding: 0;
    overflow: hidden;
  }
}

.brd-error {
  padding: 4rem 2rem;
  text-align: center;

  &__icon {
    color: $danger;
    opacity: 0.7;
    margin-bottom: 1rem;
  }

  h5 {
    font-weight: 600;
  }
}
</style>
